/* eslint-disable @typescript-eslint/indent */
import { getBFFApiClient } from '@acp/feature/auth';
import {
    APIResponse, ReportedFinding,
    ServiceShortName,
    ReportedFindingTransform,
    DropdownServicingCategory,
} from '@acp/shared';

import HttpStatusCodes from 'http-status-codes';
import { ReportedFindingsFilter } from './types/filters';

async function getReportedFindings(
    params: ReportedFindingsFilter,
    pageNumber: number,
    pageSize: number
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryParams: any = {
        query: params.query ?? '',
        facet: params.facets,
        service: params.services,
        serviceExclusivity: params.serviceExclusivity,
        page: pageNumber,
        propertyId: params.propertyId,
        'property-manager': [params.propertyManager],
        category: params.servicingCategories,
        'page-size': pageSize,
    };

    const response = await getBFFApiClient().get<
        APIResponse<{
            total: number;
            items: ReportedFinding[];
            unfilteredTotal: number;
        }>
    >('/v1/reported-findings', {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: queryParams,
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get active reported findings.');
    }

    return response.data.data;
}

async function getPropertyReportedFindings(propertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<ReportedFinding[]>>(
        `/v1/property/${propertyId}/reported-findings`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get active reported findings.');
    }

    return response.data.data;
}

async function getPropertyReportedFindingsWizard(
    propertyId: number,
    serviceShortName: ServiceShortName | string
) {
    const response = await getBFFApiClient().get<APIResponse<ReportedFindingTransform>>(`/v2/property/${propertyId}/reported-findings/${serviceShortName}`);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get active reported findings.');
    }

    return response.data.data;
}

async function requestQuoteReportedFindings(
    compliancePropertyId: number,
    problemRegisterIds: number[]
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(`/v2/property/${compliancePropertyId}/reported-findings/quote`, {
        compliancePropertyId,
        problemRegisterIds,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to submit a quote from reported findings.');
    }

    return response.data.data;
}

async function informDIRequest(
    compliancePropertyId: number,
    problemRegisterIds: number[],
    notes: string
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(`/v2/property/${compliancePropertyId}/reported-findings/inform-di`, {
        compliancePropertyId,
        problemRegisterIds,
        notes,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to inform DI from reported findings.');
    }

    return response.data.data;
}

async function resolveBuildYearRequest(
    compliancePropertyId: number,
    problemRegisterIds: number[],
    propertyBuilt?: Date,
    notes?: string
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(`/v2/property/${compliancePropertyId}/reported-findings/resolve-build-year`, {
        compliancePropertyId,
        problemRegisterIds,
        propertyBuilt,
        notes,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to provide built year from reported findings.');
    }

    return response.data.data;
}

async function submitReportedFindingsDetails(
    compliancePropertyId: number,
    selectedProblemRegisterIds: number[],
    documentIds: string[],
    notes?: string
) {
    const response = await getBFFApiClient().post<APIResponse<null>>(`/v2/property/${compliancePropertyId}/reported-findings`, {
        compliancePropertyId,
        selectedProblemRegisterIds,
        documentIds,
        notes,
    });

    if (!response.data.success) {
        throw new Error(response.data.error);
    }

    if (response.status !== HttpStatusCodes.OK) {
        throw new Error('Failed to submit active reported findings.');
    }

    return response.data.data;
}

async function dismissReportedFinding(reportedFindingId: number) {
    const response = await getBFFApiClient().post<APIResponse<{ success: boolean }>>(
        `/v1/reported-findings/${reportedFindingId}/dismiss`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(response.data?.error ?? 'Failed to mark this property as vacant');
    }

    return response.data.data;
}

async function unDismissReportedFinding(reportedFindingId: number) {
    const response = await getBFFApiClient().post<APIResponse<{ success: boolean }>>(
        `/v1/reported-findings/${reportedFindingId}/restore`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(response.data?.error ?? 'Failed to mark this property as vacant');
    }

    return response.data.data;
}

async function getUrgentReportedFindings(propertyManagerId?: string) {
    const response = await getBFFApiClient().get<
        APIResponse<{
            total: number;
            items: ReportedFinding[];
        }>
    >('/v1/reported-findings/analytics/urgent', {
        params: {
            propertyManagerId,
        },
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get urgent reported findings.');
    }

    return response.data.data;
}

async function getServicingCategories() {
    const response = await getBFFApiClient().get<
        APIResponse<DropdownServicingCategory[]>
    >('/v1/reported-findings/servicing-categories');

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get servicing categories: ${response.data.error}`);
    }

    return response.data.data;
}

export {
    getReportedFindings,
    getPropertyReportedFindings,
    requestQuoteReportedFindings,
    submitReportedFindingsDetails,
    getPropertyReportedFindingsWizard,
    getUrgentReportedFindings,
    dismissReportedFinding,
    unDismissReportedFinding,
    informDIRequest,
    resolveBuildYearRequest,
    getServicingCategories,
};
